import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import SEO from '@/components/site/SEO';
import { Plus, Minus, ArrowRight, CheckCircle2 } from 'lucide-react';

const faqs = [
  {
    q: 'What is the best way to get a home loan in Mumbai?',
    a: 'Compare offers from multiple banks instead of approaching one branch. Loan distributors like Finirio (Mumbai-based) compare 20+ banks, get customers a lower negotiated interest rate from 7%* thanks to bulk monthly business with banks, and cut charges to ₹30,000–₹45,000 on a ₹1 Crore file versus around ₹60,000 direct — at zero fee to the customer.',
  },
  {
    q: 'What home loan interest rate can I get in India?',
    a: 'Rates through Finirio start at 7%* per annum, varying by credit score, income, loan amount and lender. A CIBIL score above 750 gets the best rates. Applying via a bulk distributor typically beats the walk-in card rate for the same profile.',
  },
  {
    q: 'How much are home loan processing charges on a ₹1 Crore loan?',
    a: 'Directly at a bank, total charges on a ₹1 Crore file are typically around ₹60,000. Through Finirio the customer pays about ₹30,000–₹45,000 and Finirio pays the remaining difference to the bank on the customer\u2019s behalf.',
  },
  {
    q: 'What documents are needed for a home loan in India?',
    a: 'KYC (PAN, Aadhaar), income proof (salary slips or ITRs), 6–12 months bank statements, and property documents. Finirio provides doorstep document collection so no branch visits are needed.',
  },
  {
    q: 'How long does home loan disbursal take?',
    a: 'Through Finirio, sanction typically takes 3–5 working days after documents are submitted and disbursal happens within about 7 days.',
  },
  {
    q: 'What is the maximum home loan tenure and funding?',
    a: 'Tenure up to 30 years and up to 100% property funding on select cases, with age eligibility up to 70 years and no upper cap on loan amount through Finirio\u2019s partner banks.',
  },
  {
    q: 'Can I transfer my existing home loan to a lower rate?',
    a: 'Yes. A balance transfer moves your existing home loan to a bank offering a lower rate. Finirio negotiates the new rate using its bulk-business leverage and handles the process free of charge.',
  },
  {
    q: 'Is a home loan agent or distributor free in India?',
    a: 'Legitimate distributors like Finirio are free for customers — banks pay them a marketing/business fee for the loan volume they bring. Never pay an upfront \u201cagent fee\u201d for a loan.',
  },
  {
    q: 'Who is eligible for a home loan in India?',
    a: 'Salaried employees, self-employed professionals and business owners aged 21–70 with stable income and a good credit score. A minimum monthly income of ₹25,000 is recommended.',
  },
  {
    q: 'Which banks give the lowest home loan rates in Mumbai?',
    a: 'Rates change monthly per bank. Finirio compares live offers across 20+ partner banks — including HDFC, SBI, ICICI, Kotak, Axis, Standard Chartered, IDFC First and Bank of Baroda — and matches each profile to the lowest available negotiated rate.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const keyFacts = [
  'Interest rates from 7%* p.a. via negotiated bulk offers',
  'Tenure up to 30 years; age eligibility up to 70 years',
  'Up to 100% property funding on select cases; no upper cap on amount',
  'Direct bank charges on ₹1 Cr file: ~₹60,000 — via Finirio: ₹30,000–₹45,000',
  'CIBIL score above 750 gets the best rates',
  'Sanction in 3–5 working days; disbursal within ~7 days',
];

const steps = [
  ['Check eligibility', 'Free eligibility check based on income, obligations and property value.'],
  ['Compare bank offers', 'Negotiated rates from 20+ banks matched to your profile.'],
  ['Doorstep documentation', 'KYC, income proof and property papers collected from your home.'],
  ['Sanction in 3–5 working days', 'A dedicated relationship manager follows up with the bank for you.'],
  ['Disbursal within ~7 days', 'With the banker directly reachable throughout the transaction.'],
];

const HomeLoanGuide = () => {
  const [open, setOpen] = useState(0);
  return (
    <main>
      <SEO
        title="Home Loan in Mumbai & India: Rates from 7%*, Charges, Process & FAQ | Finirio"
        description="Complete home loan guide for Mumbai & India: rates from 7%*, why charges via Finirio are ₹30–45k vs ₹60k on a ₹1 Crore file, eligibility, documents, 7-day disbursal, and 10 FAQs answered."
        path="/home-loan-guide-mumbai"
        keywords="home loan mumbai, best home loan rate india, home loan guide, home loan charges india, home loan process, home loan documents, home loan balance transfer, cibil score home loan"
        jsonLd={jsonLd}
        jsonLdId="hl-guide-jsonld"
      />
      <PageBanner title="Home Loan Guide — Mumbai & India" crumbs={['Home Loan Guide']} />

      {/* Direct answer block */}
      <section className="bg-white py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6">
          <h1 className="font-heading text-[28px] md:text-[36px] font-bold text-slate-900 tracking-tight leading-tight text-center">
            Home Loan in Mumbai &amp; India: Complete Guide to Rates, Charges and Process
          </h1>
          <p className="mt-5 text-slate-700 text-[16px] md:text-[17px] leading-relaxed text-center">
            The cheapest way to get a home loan in Mumbai or anywhere in India is to compare negotiated offers across
            many banks rather than accepting one branch&apos;s walk-in rate. Finirio compares 20+ partner banks, secures
            rates from <b>7%*</b> using its bulk monthly business with banks, and cuts charges on a ₹1 Crore file from
            ~₹60,000 to <b>₹30,000–₹45,000</b> — free of cost to the customer.
          </p>
        </div>
      </section>

      {/* Key facts */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6">
          <h2 className="font-heading text-[24px] md:text-[30px] font-bold text-slate-900 text-center mb-8">
            Home loan key facts
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {keyFacts.map((f, i) => (
              <li key={i} className="card-lux flex items-start gap-3 p-4 text-[15px] text-slate-800">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-none" style={{ color: '#2ea690' }} />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How lower rate works */}
      <section className="bg-white py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6">
          <h2 className="font-heading text-[24px] md:text-[30px] font-bold text-slate-900 text-center mb-6">
            How do I get a lower home loan interest rate?
          </h2>
          <p className="text-slate-700 text-[16px] leading-relaxed">
            Banks give their best rates to channels that bring volume. When 5–6 friends approach a Branch Manager
            together, they negotiate a better rate than one individual. Finirio applies the same principle at scale:
            it routes a large volume of loan files to partner banks every month, and in return those banks price
            Finirio customers below the standard walk-in rate. The bank&apos;s own banker stays in the transaction, so
            terms are fully transparent. Read more on the{' '}
            <Link to="/why-finirio" className="text-[#1e73be] font-semibold underline">Why Finirio</Link> page.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6">
          <h2 className="font-heading text-[24px] md:text-[30px] font-bold text-slate-900 text-center mb-8">
            Step-by-step: getting a home loan through Finirio
          </h2>
          <ol className="space-y-4">
            {steps.map(([t, d], i) => (
              <li key={i} className="card-lux flex items-start gap-4 p-5">
                <span className="w-9 h-9 rounded-full grid place-items-center flex-none font-bold text-[15px] text-[#16233a]" style={{ background: 'linear-gradient(135deg,#e8c887,#c9a24b)' }}>
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-heading font-bold text-slate-900 text-[16px]">{t}</h3>
                  <p className="text-slate-600 text-[14px] mt-1">{d}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/home-loan" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-white text-[15px] font-semibold hover:brightness-110 transition" style={{ backgroundColor: '#1e73be' }}>
              Apply for Home Loan <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/emi-calculator" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-[15px] font-semibold border border-[#1e73be] text-[#1e73be] hover:bg-[#1e73be]/5 transition">
              Calculate EMI
            </Link>
            <Link to="/eligibility-calculator" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-[15px] font-semibold border border-slate-300 text-slate-800 hover:bg-slate-50 transition">
              Check Eligibility
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14">
        <div className="max-w-[900px] mx-auto px-4 lg:px-6">
          <h2 className="font-heading text-[24px] md:text-[30px] font-bold text-slate-900 text-center mb-8">
            Home loan FAQs — Mumbai &amp; India
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className={`border rounded-lg transition-colors ${isOpen ? 'border-[#1e73be]/40 bg-[#f4faff]' : 'border-slate-200 bg-white'}`}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between text-left px-5 py-4" aria-expanded={isOpen}>
                    <span className="font-heading font-semibold text-slate-900 text-[15px] md:text-[16px] pr-4">{f.q}</span>
                    <span className="w-8 h-8 rounded-full grid place-items-center flex-none" style={{ backgroundColor: isOpen ? '#1e73be' : '#eaf3fb', color: isOpen ? 'white' : '#1e73be' }}>
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-slate-700 text-[15px] leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomeLoanGuide;
